import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mic, Square, Trash2, Save, X, Clock, Phone } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// Inline voice recorder: record → listen → name → save and/or convert
const AudioRecordModal = ({ onClose, onSave, saveLabel = "Save Recording", onConvert = null, convertLabel = "Convert to Text", callPhone = null, callName = "" }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [name, setName] = useState("");
  const [busyAction, setBusyAction] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Stop the mic if the modal closes mid-recording
  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        const now = new Date();
        setName(`Voice note ${now.toISOString().slice(0, 10)} ${now.toTimeString().slice(0, 5)}`);
      };
      rec.start();
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      setRecording(true);
    } catch {
      toast.error("Microphone access denied — allow the microphone and try again");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      clearInterval(timerRef.current);
      setRecording(false);
    }
  };

  const callWhileRecording = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (callPhone) window.location.href = `tel:${callPhone}`;
  };


  const discard = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
  };

  const buildFile = () => {
    const ext = (audioBlob.type.split("/")[1] || "webm").split(";")[0];
    return new File([audioBlob], `${name || "Voice note"}.${ext}`, { type: audioBlob.type });
  };

  const runAction = async (action, handler) => {
    if (!audioBlob || !handler) return;
    setBusyAction(action);
    try {
      await handler(buildFile(), name);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    } finally {
      setBusyAction(null);
    }
  };

  const save = () => runAction("save", onSave);
  const convert = () => runAction("convert", onConvert);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="record-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card rounded-sm p-6 w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif font-semibold text-lg">Record Audio</h3>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white transition-colors" data-testid="record-modal-close">
            <X size={18} />
          </button>
        </div>

        {!audioBlob ? (
          <div className="flex flex-col items-center py-6">
            <button
              onClick={recording ? stopRecording : startRecording}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                recording ? "bg-red-500 animate-pulse" : "bg-[#D4AF37] hover:bg-[#c19b2e]"
              }`}
              data-testid="modal-record-btn"
            >
              {recording
                ? <Square size={32} className="text-white" fill="white" />
                : <Mic size={36} className="text-black" />
              }
            </button>
            <p className="mt-4 text-sm text-white/60 flex items-center gap-2" data-testid="modal-record-status">
              <Clock size={14} className="trade-gold" />
              {recording ? `Recording... ${fmtTime(seconds)}` : "Tap the mic to start recording"}
            </p>
            {recording && callPhone && (
              <div className="mt-5 w-full">
                <button
                  type="button"
                  onClick={callWhileRecording}
                  className="w-full bg-[#10b981]/10 trade-green border border-[#10b981]/25 hover:bg-[#10b981]/20 rounded-sm h-11 text-sm flex items-center justify-center gap-2"
                  data-testid="modal-call-now-btn"
                >
                  <Phone size={15} /> Call {callName} Now
                </button>
                <p className="text-[11px] text-white/35 text-center mt-2">
                  Keep this page open if your phone allows it, then return to stop and save.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <audio controls src={audioUrl} className="w-full" data-testid="modal-audio-playback" />
            <div>
              <Label className="text-xs text-white/60">Recording name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="trade-input mt-1 h-11 rounded-sm" data-testid="modal-audio-name" />
            </div>
            <div className="flex gap-3">
              <Button onClick={save} disabled={!!busyAction} className="flex-1 trade-btn-primary rounded-sm h-12" data-testid="modal-save-audio">
                <Save size={16} className="mr-2" />
                {busyAction === "save" ? "Saving..." : saveLabel}
              </Button>
              {onConvert && (
                <Button
                  onClick={convert}
                  disabled={!!busyAction}
                  className="flex-1 bg-white/10 text-white hover:bg-white/20 rounded-sm h-12"
                  data-testid="modal-convert-audio"
                >
                  {busyAction === "convert" ? "Writing..." : convertLabel}
                </Button>
              )}
              <Button onClick={discard} disabled={!!busyAction} className="bg-white/10 text-white hover:bg-white/20 rounded-sm h-12" data-testid="modal-discard-audio">
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AudioRecordModal;
